import { useMemo, useState, type CSSProperties } from 'react'
import { useNavigate } from 'react-router-dom'
import { Banknote, Check, Eye, Flag, Lock, Search, ShoppingCart, Wallet, Receipt } from 'lucide-react'
import { Button } from '@/components/ui'
import { SectionHeader, QuickStatCard } from '@/components/hearth'
import {
  AccountsList,
  ConnectionsList,
  TransactionsList,
  PlaidLinkButton,
  CategoryIconChip,
} from '@/components/financial'
import { ParentSelector } from '@/components/family'
import { PagePeaceState } from '@/components/common'
import { AttentionNeededState } from '@/components/dashboard'
import {
  useFamily,
  useFinancialConnections,
  useTransactions,
  useSpendingSummary,
  usePermissions,
  useRoleBasedMemberId,
  useAlerts,
} from '@/hooks'
import type { Alert, Transaction } from '@/types'

type SpendingPeriod = '7d' | '30d' | '90d'

const periodLabels: Record<SpendingPeriod, string> = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
}

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

function formatAmount(amount: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Math.abs(amount))
}

function formatShortDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
}

function stagger(n: number): CSSProperties {
  return { '--stagger': n } as CSSProperties
}

/** "How money watch works" — shown before any account is connected. */
function HowMoneyWatchWorks({ isParent }: { isParent: boolean }) {
  const rows = [
    {
      icon: Eye,
      chipClass: 'bg-primary-600/10 text-primary-600',
      text: 'FamSteady quietly watches transactions for anything unusual',
    },
    {
      icon: Search,
      chipClass: 'bg-warning-500/10 text-warning-600',
      text: 'Suspicious charges — like scams targeting seniors — are flagged for review',
    },
    {
      icon: Lock,
      chipClass: 'bg-success-500/10 text-success-600',
      text: 'Connections are read-only and encrypted; no one can move money',
    },
  ]

  return (
    <section className="space-y-3">
      <SectionHeader title="How money watch works" />
      <div className="hearth-card divide-y divide-gray-100">
        {rows.map(({ icon: Icon, chipClass, text }) => (
          <div key={text} className="flex items-center gap-3 px-4 py-3.5">
            <span
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${chipClass}`}
            >
              <Icon className="h-5 w-5" />
            </span>
            <p className="text-sm text-gray-600">{text}</p>
          </div>
        ))}
      </div>
      {isParent ? (
        <PlaidLinkButton />
      ) : (
        <p className="text-sm text-gray-500">
          Connections will appear here when a parent links their accounts.
        </p>
      )}
    </section>
  )
}

/** A flagged/suspicious transaction row inside "Worth a look". */
function WorthALookRow({
  transaction,
  onFlag,
  onUnflag,
}: {
  transaction: Transaction
  onFlag: (id: string) => void
  onUnflag: (id: string) => void
}) {
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <CategoryIconChip category={transaction.category} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <p className="truncate text-sm font-medium text-gray-900">
            {transaction.merchant_name || transaction.description}
          </p>
          {transaction.flagged && (
            <Flag
              className="h-3.5 w-3.5 shrink-0 text-warning-600"
              fill="currentColor"
              aria-label="Flagged"
            />
          )}
        </div>
        <p className="text-xs text-gray-500">
          {formatShortDate(transaction.date)}
          {transaction.flagged ? ' · Flagged for review' : ' · Unusual pattern'}
        </p>
      </div>
      <span className="shrink-0 font-rounded text-sm font-semibold tabular-nums text-gray-900">
        {transaction.amount < 0 ? '−' : '+'}
        {formatAmount(transaction.amount)}
      </span>
      {transaction.flagged ? (
        <Button variant="ghost" size="sm" onClick={() => onUnflag(transaction.id)}>
          Looks fine
        </Button>
      ) : (
        <Button variant="secondary" size="sm" onClick={() => onFlag(transaction.id)}>
          Flag
        </Button>
      )}
    </div>
  )
}

export function FinancialOverviewPage() {
  const navigate = useNavigate()
  const [period, setPeriod] = useState<SpendingPeriod>('30d')
  const { selectedFamilyId } = useFamily()
  const { parentUserId, shouldShowMemberSelector } = useRoleBasedMemberId()
  const { canViewFinancial, isParent } = usePermissions()

  // Unread financial alerts
  const { alerts, resolveAlert, isResolving } = useAlerts(selectedFamilyId, {
    status: 'UNREAD',
    alert_type: 'FINANCIAL',
    page_size: 5,
  })

  const {
    connections,
    isLoading: isLoadingConnections,
    sync,
    disconnect,
    isSyncing,
  } = useFinancialConnections(selectedFamilyId, isParent ? parentUserId : undefined)

  const visibleConnections = isParent || !parentUserId
    ? connections
    : connections.filter((connection) => connection.parent_user === parentUserId)

  const {
    transactions,
    pagination: transactionsPagination,
    isLoading: isLoadingTransactions,
    flagTransaction,
    unflagTransaction,
  } = useTransactions(selectedFamilyId, { page_size: 5, parent_user_id: parentUserId })

  // Flagged transactions get their own query so the count and "Worth a look"
  // list aren't limited to whatever appears in the 5 most recent.
  const { transactions: flaggedTransactions, pagination: flaggedPagination } = useTransactions(
    selectedFamilyId,
    { page_size: 5, flagged: true, parent_user_id: parentUserId }
  )

  const { data: spendingSummary } = useSpendingSummary(selectedFamilyId, period, parentUserId)

  const hasConnections = connections.length > 0
  const hasAlerts = alerts.length > 0

  const totalSpending = spendingSummary?.total ?? 0

  const accounts = visibleConnections.flatMap((connection) => connection.accounts)
  const balances = accounts.filter((account) => account.balance !== undefined)
  const totalBalance = balances.reduce((sum, account) => sum + (account.balance ?? 0), 0)

  const transactionCount = transactionsPagination?.total_count ?? transactions.length
  const flaggedCount = flaggedPagination?.total_count ?? flaggedTransactions.length

  // Flagged first, then suspicious-but-unflagged from recent activity, up to 5.
  const worthALook = useMemo(() => {
    const seen = new Set(flaggedTransactions.map((t) => t.id))
    const suspicious = transactions.filter(
      (t) => !t.flagged && t.anomaly_score > 0.7 && !seen.has(t.id)
    )
    return [...flaggedTransactions, ...suspicious].slice(0, 5)
  }, [flaggedTransactions, transactions])

  const allQuiet = !hasAlerts && worthALook.length === 0

  const handleFlag = async (transactionId: string) => {
    await flagTransaction({
      transactionId,
      data: { reason: 'SUSPICIOUS' },
    })
  }

  const handleUnflag = async (transactionId: string) => {
    await unflagTransaction(transactionId)
  }

  const handleReviewAlert = (alert: Alert) => {
    navigate(`/alerts?id=${alert.id}`)
  }

  const handleDismissAlert = async (alert: Alert) => {
    await resolveAlert({
      alertId: alert.id,
      data: { resolution: 'NO_ACTION_NEEDED' },
    })
  }

  // Permission gate
  if (!canViewFinancial) {
    return (
      <div className="hearth-appear flex flex-col items-center justify-center py-16 text-center" style={stagger(0)}>
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100">
          <Wallet className="h-8 w-8 text-primary-600" />
        </span>
        <h3 className="mt-4 font-serif text-xl font-semibold text-gray-900">
          You don't have access yet
        </h3>
        <p className="mt-2 max-w-sm text-sm text-gray-500">
          Ask a family admin to grant you permission to view financial information.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 1. Page header */}
      <div
        className="hearth-appear flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
        style={stagger(0)}
      >
        <div>
          <h1 className="font-serif text-2xl font-semibold text-gray-900">Money</h1>
          <p className="text-gray-500">We're watching for unusual activity.</p>
        </div>
        <div className="flex items-center gap-3">
          {shouldShowMemberSelector && <ParentSelector />}
          {/* Period selector */}
          <div className="hearth-card flex p-1">
            {(Object.keys(periodLabels) as SpendingPeriod[]).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                  period === p
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {periodLabels[p]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Hero: attention when alerts, peace when all quiet */}
      {hasAlerts ? (
        <div className="hearth-appear" style={stagger(1)}>
          <AttentionNeededState
            alerts={alerts}
            totalCount={alerts.length}
            onReview={handleReviewAlert}
            onDismiss={handleDismissAlert}
            isDismissing={isResolving}
          />
        </div>
      ) : (
        hasConnections &&
        allQuiet && (
          <div className="hearth-appear" style={stagger(1)}>
            <PagePeaceState
              icon={Wallet}
              title="Finances look good"
              description="No unusual transactions detected. We'll let you know if anything seems off."
              variant="success"
            />
          </div>
        )
      )}

      {hasConnections || isLoadingConnections ? (
        <>
          {/* 2. Bank connections */}
          <section className="hearth-appear space-y-3" style={stagger(2)}>
            <SectionHeader title="Bank connections" />
            <ConnectionsList
              connections={visibleConnections}
              isLoading={isLoadingConnections}
              canManage={isParent}
              onSync={isParent ? sync : undefined}
              onDisconnect={isParent ? disconnect : undefined}
              isSyncing={isSyncing}
            />
            {isParent && <PlaidLinkButton size="sm" />}
          </section>

          {/* 4. Summary grid */}
          <div
            className="hearth-appear grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
            style={stagger(3)}
          >
            <QuickStatCard
              icon={Banknote}
              iconClassName="bg-success-500/10 text-success-600"
              title="Balance"
              value={balances.length > 0 ? formatCurrency(totalBalance) : '—'}
              subtitle="Monitored accounts"
            />
            <QuickStatCard
              icon={ShoppingCart}
              iconClassName="bg-primary-600/10 text-primary-600"
              title="Spending"
              value={formatCurrency(totalSpending)}
              subtitle={periodLabels[period]}
            />
            <QuickStatCard
              icon={Receipt}
              iconClassName="bg-info-500/10 text-info-500"
              title="Transactions"
              value={transactionCount.toString()}
              subtitle="Recent"
            />
            <QuickStatCard
              icon={Flag}
              iconClassName={
                flaggedCount > 0
                  ? 'bg-warning-500/10 text-warning-600'
                  : 'bg-gray-400/10 text-gray-500'
              }
              title="Flagged"
              value={flaggedCount.toString()}
              subtitle={flaggedCount > 0 ? 'Review' : 'All clear'}
            />
          </div>

          {/* 5. Worth a look */}
          <section className="hearth-appear space-y-3" style={stagger(4)}>
            <SectionHeader title="Worth a look" />
            {worthALook.length > 0 ? (
              <div className="hearth-card divide-y divide-gray-100">
                {worthALook.map((transaction) => (
                  <WorthALookRow
                    key={transaction.id}
                    transaction={transaction}
                    onFlag={handleFlag}
                    onUnflag={handleUnflag}
                  />
                ))}
              </div>
            ) : (
              <div className="hearth-card flex items-center gap-3 px-4 py-3.5">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-success-100 text-success-600">
                  <Check className="h-4 w-4" />
                </span>
                <p className="text-sm text-gray-600">No unusual transactions detected</p>
              </div>
            )}
          </section>

          {/* 6. Accounts */}
          <section className="hearth-appear space-y-3" style={stagger(5)}>
            <SectionHeader title="Accounts" />
            <AccountsList connections={visibleConnections} isLoading={isLoadingConnections} />
          </section>

          {/* 7. Recent transactions */}
          <section className="hearth-appear space-y-3" style={stagger(6)}>
            <SectionHeader
              title="Recent transactions"
              actionTitle="See all"
              onAction={() => navigate('/financial/transactions')}
            />
            <div className="hearth-card overflow-hidden">
              <TransactionsList
                transactions={transactions.slice(0, 5)}
                isLoading={isLoadingTransactions}
                onFlag={handleFlag}
                onUnflag={handleUnflag}
              />
            </div>
          </section>
        </>
      ) : (
        /* 3. No accounts connected yet */
        <div className="hearth-appear" style={stagger(2)}>
          <HowMoneyWatchWorks isParent={isParent} />
        </div>
      )}
    </div>
  )
}
